import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, Mail, Building2, MapPin, RefreshCw, AlertTriangle, CheckCircle2, XCircle, MessageCircle, Filter, Clock, Trash2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { API, formatPrice, formatDate } from '../utils';

const STEP_PROGRESS = {
  1: { pct: 20, color: 'bg-slate-500', label: 'Choix du pack' },
  2: { pct: 40, color: 'bg-orange-500', label: 'Dates' },
  3: { pct: 60, color: 'bg-yellow-500', label: 'Informations' },
  4: { pct: 80, color: 'bg-emerald-500', label: 'Contrat & signature' },
  5: { pct: 95, color: 'bg-emerald-600', label: 'Paiement' },
};

const FOLLOW_UP_LABELS = {
  new: { label: 'À rappeler', cls: 'bg-yellow-500/20 text-yellow-300' },
  contacted: { label: 'Contacté', cls: 'bg-blue-500/20 text-blue-300' },
  converted: { label: 'Converti', cls: 'bg-emerald-500/20 text-emerald-300' },
  no_interest: { label: 'Pas intéressé', cls: 'bg-slate-500/30 text-slate-300' },
  unreachable: { label: 'Injoignable', cls: 'bg-red-500/20 text-red-300' },
};

const timeAgo = (iso) => {
  if (!iso) return '-';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h}h`;
  const d = Math.floor(h / 24);
  return `il y a ${d}j`;
};

const fmtDuration = (start, end) => {
  if (!start) return '-';
  const a = new Date(start).getTime();
  const b = end ? new Date(end).getTime() : Date.now();
  const sec = Math.max(0, Math.floor((b - a) / 1000));
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m} min ${sec % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60} min`;
};

const AbandonedReservationsView = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('new');
  const [includeRecent, setIncludeRecent] = useState(false);
  const [followUpModal, setFollowUpModal] = useState(null); // {session_id, name} | null
  const [followUpStatus, setFollowUpStatus] = useState('contacted');
  const [followUpNotes, setFollowUpNotes] = useState('');
  const [actionBusy, setActionBusy] = useState(null);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.set('follow_up_status', filter);
      if (includeRecent) params.set('include_recent', 'true');
      const r = await axios.get(`${API}/admin/abandoned-reservations?${params.toString()}`, { withCredentials: true });
      setItems(r.data || []);
    } catch (e) {
      toast.error('Impossible de charger les réservations abandonnées');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, [filter, includeRecent]);

  const deleteItem = async (session_id, name) => {
    if (!window.confirm(`Supprimer définitivement « ${name} » de la liste des réservations abandonnées ?\n\nCette entrée disparaîtra de cette liste. La commande ou le devis éventuel associé n'est PAS supprimé.`)) return;
    setActionBusy(session_id);
    try {
      await axios.delete(`${API}/admin/abandoned-reservations/${session_id}`, { withCredentials: true });
      toast.success('Entrée supprimée de la liste');
      await fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setActionBusy(null);
    }
  };

  const submitFollowUp = async () => {
    if (!followUpModal) return;
    setActionBusy(followUpModal.session_id);
    try {
      await axios.post(`${API}/admin/abandoned-reservations/${followUpModal.session_id}/follow-up`,
        { status: followUpStatus, notes: followUpNotes.trim() },
        { withCredentials: true });
      toast.success('Statut mis à jour');
      setFollowUpModal(null);
      setFollowUpNotes('');
      setFollowUpStatus('contacted');
      await fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setActionBusy(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="abandoned-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-400" />
            Réservations abandonnées
            {items.length > 0 && filter === 'new' && (
              <span className="ml-1 px-2 py-0.5 rounded-full bg-yellow-400 text-black text-xs font-bold" data-testid="abandoned-count-badge">
                {items.length}
              </span>
            )}
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Clients qui ont commencé une réservation sans finaliser — vos commerciaux peuvent les rappeler.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-slate-400">
            <input
              type="checkbox"
              checked={includeRecent}
              onChange={(e) => setIncludeRecent(e.target.checked)}
              className="w-3.5 h-3.5"
            />
            Inclure récentes (&lt;15 min)
          </label>
          <Button variant="outline" size="sm" className="border-slate-700 text-slate-300" onClick={fetchItems}>
            <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
          </Button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-2 border-b border-slate-800 overflow-x-auto">
        <Filter className="w-4 h-4 text-slate-500 mb-2.5 ml-1" />
        {[
          { k: 'new', label: '🔔 À rappeler' },
          { k: 'contacted', label: 'Contactés' },
          { k: 'converted', label: '✅ Convertis' },
          { k: 'no_interest', label: 'Pas intéressés' },
          { k: 'unreachable', label: 'Injoignables' },
          { k: 'all', label: 'Toutes' },
        ].map(t => (
          <button
            key={t.k}
            onClick={() => setFilter(t.k)}
            className={`px-3 py-2 text-sm font-medium transition border-b-2 -mb-px whitespace-nowrap ${
              filter === t.k
                ? 'border-yellow-400 text-yellow-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
            data-testid={`abandoned-filter-${t.k}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-400">Chargement...</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <CheckCircle2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
            Aucune réservation abandonnée dans cette catégorie. 🎉
          </div>
        ) : (
          <div className="divide-y divide-slate-800">
            {items.map(i => {
              const customer = i.customer || {};
              const name = `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || customer.company || 'Visiteur anonyme';
              const step = STEP_PROGRESS[i.current_step] || { pct: 0, color: 'bg-slate-500', label: '?' };
              const fu = FOLLOW_UP_LABELS[i.follow_up_status || 'new'] || FOLLOW_UP_LABELS.new;
              const busy = actionBusy === i.session_id;
              const hasContactInfo = customer.email || customer.phone;
              return (
                <div key={i.session_id} className="p-4 hover:bg-slate-800/30 transition" data-testid={`abandoned-row-${i.session_id}`}>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Customer column */}
                    <div>
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-semibold text-white">{name}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${fu.cls}`}>{fu.label}</span>
                        {i.source === 'reservation' && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] uppercase font-bold bg-orange-500/15 text-orange-300 border border-orange-500/30" title="Réservation créée mais paiement non finalisé">
                            Réservation orpheline
                          </span>
                        )}
                        {i.source === 'manual_order' && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] uppercase font-bold bg-purple-500/15 text-purple-300 border border-purple-500/30" title={`Devis ${i._quote_number || ''} - ${i._commercial || ''}`.trim()}>
                            Devis {i._quote_number || ''}
                          </span>
                        )}
                        {i.source === 'tracker' && (
                          <span className="px-1.5 py-0.5 rounded text-[9px] uppercase font-bold bg-blue-500/15 text-blue-300 border border-blue-500/30" title="Détecté en direct depuis le tunnel">
                            Live
                          </span>
                        )}
                      </div>
                      <div className="space-y-1 text-xs text-slate-400">
                        {customer.email && (
                          <a href={`mailto:${customer.email}`} className="flex items-center gap-1.5 text-slate-300 hover:text-yellow-300">
                            <Mail className="w-3 h-3" /> {customer.email}
                          </a>
                        )}
                        {customer.phone && (
                          <a href={`tel:${customer.phone}`} className="flex items-center gap-1.5 text-slate-300 hover:text-yellow-300">
                            <Phone className="w-3 h-3" /> {customer.phone}
                          </a>
                        )}
                        {customer.company && (
                          <div className="flex items-center gap-1.5"><Building2 className="w-3 h-3" /> {customer.company}</div>
                        )}
                        {(customer.city || customer.postalCode) && (
                          <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {customer.postalCode} {customer.city}</div>
                        )}
                      </div>
                    </div>

                    {/* Funnel column */}
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Progression</div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex-1 h-2 rounded-full bg-slate-800 overflow-hidden">
                          <div className={`h-full ${step.color}`} style={{ width: `${step.pct}%` }} />
                        </div>
                        <span className="text-xs text-white font-mono">{i.current_step}/5</span>
                      </div>
                      <div className="text-xs text-yellow-300 font-medium">{step.label}</div>
                      <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Temps passé : {fmtDuration(i.started_at, i.updated_at)}
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {i.pandadoc_signed && <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300">✓ Contrat signé</span>}
                        {i.signature_drawn && !i.pandadoc_signed && <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300">✓ Signature dessinée</span>}
                        {i.payment_method_selected === 'sepa' && <span className="text-[10px] px-2 py-0.5 rounded bg-blue-500/20 text-blue-300">Mode SEPA choisi</span>}
                        {i.payment_method_selected === 'card' && <span className="text-[10px] px-2 py-0.5 rounded bg-blue-500/20 text-blue-300">Mode CB choisi</span>}
                      </div>
                    </div>

                    {/* Pack & price column */}
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Pack</div>
                      <div className="text-white text-sm font-semibold">{i.pack_name || 'Non choisi'}</div>
                      {i.pack_surface && <div className="text-xs text-slate-400">{i.pack_surface}</div>}
                      <div className="mt-1 text-xs space-y-0.5">
                        {i.monthly_price > 0 && <div className="text-slate-300">{formatPrice(i.monthly_price)} <span className="text-slate-500">/mois</span></div>}
                        {i.caution > 0 && <div className="text-slate-500">Caution : {formatPrice(i.caution)}</div>}
                        {i.rental_start_date && (
                          <div className="text-slate-500">Du {i.rental_start_date} {i.rental_end_date ? `au ${i.rental_end_date}` : ''}</div>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-2">
                        Dernière activité : <strong className="text-slate-400">{timeAgo(i.updated_at)}</strong>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                    {i.follow_up_notes && (
                      <div className="text-xs text-slate-400 italic max-w-md truncate" title={i.follow_up_notes}>
                        💬 {i.follow_up_notes} {i.follow_up_by && <span className="text-slate-600">— {i.follow_up_by}</span>}
                      </div>
                    )}
                    <div className="flex gap-2 ml-auto">
                      {customer.phone && (
                        <a href={`tel:${customer.phone}`}
                          className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300"
                          data-testid={`abandoned-call-${i.session_id}`}
                        >
                          <Phone className="w-3 h-3" /> Appeler
                        </a>
                      )}
                      {customer.email && (
                        <a href={`mailto:${customer.email}?subject=Votre réservation Phocea Rent`}
                          className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded bg-blue-500/15 hover:bg-blue-500/25 text-blue-300"
                          data-testid={`abandoned-email-${i.session_id}`}
                        >
                          <Mail className="w-3 h-3" /> Email
                        </a>
                      )}
                      {hasContactInfo && (
                        <Button
                          size="sm"
                          disabled={busy}
                          onClick={() => { setFollowUpModal({ session_id: i.session_id, name }); setFollowUpStatus(i.follow_up_status === 'new' ? 'contacted' : i.follow_up_status); setFollowUpNotes(i.follow_up_notes || ''); }}
                          className="bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 border border-yellow-500/40 text-xs"
                          data-testid={`abandoned-followup-${i.session_id}`}
                        >
                          <MessageCircle className="w-3 h-3 mr-1" /> Mettre à jour
                        </Button>
                      )}
                      <Button
                        size="sm"
                        disabled={busy}
                        onClick={() => deleteItem(i.session_id, name)}
                        className="bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/40 text-xs"
                        data-testid={`abandoned-delete-${i.session_id}`}
                      >
                        <Trash2 className="w-3 h-3 mr-1" /> Supprimer
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Follow-up modal */}
      {followUpModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setFollowUpModal(null)}>
          <div className="bg-slate-900 border border-yellow-400/30 rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="abandoned-followup-modal">
            <h3 className="text-lg font-bold text-white mb-1">Mettre à jour le statut</h3>
            <p className="text-sm text-slate-400 mb-4">{followUpModal.name}</p>

            <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Statut</label>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {[
                { k: 'contacted', label: '📞 Contacté', cls: 'border-blue-500/40 text-blue-300 hover:bg-blue-500/10' },
                { k: 'converted', label: '✅ Converti', cls: 'border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10' },
                { k: 'no_interest', label: '✗ Pas intéressé', cls: 'border-slate-600 text-slate-300 hover:bg-slate-800' },
                { k: 'unreachable', label: '☎️ Injoignable', cls: 'border-red-500/40 text-red-300 hover:bg-red-500/10' },
              ].map(o => (
                <button
                  key={o.k}
                  onClick={() => setFollowUpStatus(o.k)}
                  className={`p-2 rounded text-xs font-semibold border transition ${o.cls} ${followUpStatus === o.k ? 'ring-2 ring-yellow-400 bg-white/5' : ''}`}
                  data-testid={`abandoned-followup-status-${o.k}`}
                >
                  {o.label}
                </button>
              ))}
            </div>

            <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Note (optionnelle)</label>
            <textarea
              value={followUpNotes}
              onChange={(e) => setFollowUpNotes(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="Ex: Rappel prévu jeudi, attend devis transport..."
              className="w-full p-2 bg-slate-800 border border-slate-700 rounded text-white text-sm focus:border-yellow-400 outline-none"
              data-testid="abandoned-followup-notes"
            />
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setFollowUpModal(null)}
                className="flex-1 py-2 rounded border border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                Annuler
              </button>
              <button
                onClick={submitFollowUp}
                disabled={!followUpStatus}
                className="flex-1 py-2 rounded bg-yellow-500 hover:bg-yellow-400 text-black font-semibold disabled:opacity-40"
                data-testid="abandoned-followup-submit"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AbandonedReservationsView;
